import type { StatusData } from '../page';

type StatusLevel = 'active' | 'prepared' | 'pending' | 'partial';

interface ServiceDef {
  key: string;
  name: string;
  category: string;
  icon: string;
  iconColor: string;
  level: StatusLevel;
  statusLabel: string;
  statusNote: string;
  tenantKey?: string; // key in integrationsEnabled to show tenant count
}

const LEVEL_CFG: Record<StatusLevel, { badgeCls: string; dotCls: string; barCls: string }> = {
  active:   { badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dotCls: 'bg-emerald-400 animate-pulse', barCls: 'bg-emerald-400' },
  prepared: { badgeCls: 'bg-amber-50 text-amber-700 border-amber-200',       dotCls: 'bg-amber-400',                barCls: 'bg-amber-400' },
  pending:  { badgeCls: 'bg-slate-100 text-slate-500 border-slate-200',      dotCls: 'bg-slate-300',                barCls: 'bg-slate-300' },
  partial:  { badgeCls: 'bg-sky-50 text-sky-700 border-sky-200',             dotCls: 'bg-sky-400',                  barCls: 'bg-sky-400' },
};

const SERVICES: ServiceDef[] = [
  {
    key: 'supabase_db',
    name: 'Supabase Database',
    category: 'Core',
    icon: 'ri-database-2-line',
    iconColor: '#3b82f6',
    level: 'active',
    statusLabel: 'Active',
    statusNote: 'PostgreSQL connected. Tables, RLS, and Edge Functions ready.',
  },
  {
    key: 'supabase_auth',
    name: 'Supabase Auth',
    category: 'Core',
    icon: 'ri-shield-user-line',
    iconColor: '#3b82f6',
    level: 'active',
    statusLabel: 'Active',
    statusNote: 'Email/password auth active. Sessions managed per tenant.',
  },
  {
    key: 'vps_storage',
    name: 'VPS Storage',
    category: 'Storage',
    icon: 'ri-hard-drive-line',
    iconColor: '#f59e0b',
    level: 'prepared',
    statusLabel: 'Prepared',
    statusNote: 'Frontend config ready. Upload endpoint not deployed. Pending backend setup.',
  },
  {
    key: 'smtp2go',
    name: 'SMTP2GO Email',
    category: 'Messaging',
    icon: 'ri-mail-send-line',
    iconColor: '#6b8fd4',
    level: 'pending',
    statusLabel: 'Backend Pending',
    statusNote: 'Tenant settings UI built. Edge Function sender not deployed. Queue table ready.',
    tenantKey: 'smtp2go',
  },
  {
    key: 'whatsapp_meta',
    name: 'WhatsApp Meta API',
    category: 'Messaging',
    icon: 'ri-whatsapp-line',
    iconColor: '#4db858',
    level: 'pending',
    statusLabel: 'Backend Pending',
    statusNote: 'Config UI built. Phone Number ID / Token not wired. WhatsApp queue table ready.',
    tenantKey: 'whatsapp_meta',
  },
  {
    key: 'ai_assistant',
    name: 'AI Assistant (OpenAI)',
    category: 'AI',
    icon: 'ri-robot-line',
    iconColor: '#C8A96E',
    level: 'pending',
    statusLabel: 'Backend Pending',
    statusNote: 'UI & settings page built. API key not stored. Edge Function not deployed.',
    tenantKey: 'ai_assistant',
  },
  {
    key: 'stripe',
    name: 'Stripe Payments',
    category: 'Payments',
    icon: 'ri-bank-card-line',
    iconColor: '#a78bfa',
    level: 'prepared',
    statusLabel: 'Prepared',
    statusNote: 'Payments UI built. Secret key env var defined. Checkout Edge Function not deployed.',
    tenantKey: 'stripe',
  },
  {
    key: 'square',
    name: 'Square Payments',
    category: 'Payments',
    icon: 'ri-layout-grid-line',
    iconColor: '#f97316',
    level: 'prepared',
    statusLabel: 'Prepared',
    statusNote: 'Square UI built. Backend wiring pending.',
    tenantKey: 'square',
  },
  {
    key: 'google_calendar',
    name: 'Google Calendar',
    category: 'Scheduling',
    icon: 'ri-calendar-event-line',
    iconColor: '#34a853',
    level: 'prepared',
    statusLabel: 'Prepared',
    statusNote: 'UI scaffold built. OAuth flow and sync Edge Function not deployed.',
    tenantKey: 'google_calendar',
  },
  {
    key: 'custom_domains',
    name: 'Custom Domains',
    category: 'Website',
    icon: 'ri-earth-line',
    iconColor: '#64748b',
    level: 'pending',
    statusLabel: 'Pending DNS Setup',
    statusNote: 'Request flow built. DNS propagation backend and proxy routing not configured.',
  },
];

const CATEGORY_ORDER = ['Core', 'Storage', 'Messaging', 'AI', 'Payments', 'Scheduling', 'Website'];

interface ServiceCardProps {
  svc: ServiceDef;
  tenantCount?: number;
}

function ServiceCard({ svc, tenantCount }: ServiceCardProps) {
  const cfg = LEVEL_CFG[svc.level];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: `${svc.iconColor}18` }}>
            <i className={`${svc.icon} text-base`} style={{ color: svc.iconColor }}></i>
          </div>
          <div>
            <p className="text-slate-800 text-sm font-semibold">{svc.name}</p>
            <span className="text-slate-400 text-[10px] uppercase tracking-wider">{svc.category}</span>
          </div>
        </div>
        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-semibold whitespace-nowrap shrink-0 ${cfg.badgeCls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`}></span>
          {svc.statusLabel}
        </span>
      </div>

      {/* Note */}
      <p className="text-slate-400 text-xs leading-relaxed">{svc.statusNote}</p>

      {/* Tenant count */}
      {tenantCount !== undefined && tenantCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <i className="ri-building-2-line text-xs text-slate-400"></i>
          <span><strong className="text-slate-700">{tenantCount}</strong> tenant{tenantCount > 1 ? 's' : ''} enabled</span>
        </div>
      )}
    </div>
  );
}

interface Props { data: StatusData; }

export default function IntegrationsTab({ data }: Props) {
  // Group services by category
  const byCat: Record<string, ServiceDef[]> = {};
  SERVICES.forEach((s) => {
    (byCat[s.category] = byCat[s.category] ?? []).push(s);
  });

  // Count summary
  const activeCount   = SERVICES.filter((s) => s.level === 'active').length;
  const preparedCount = SERVICES.filter((s) => s.level === 'prepared').length;
  const pendingCount  = SERVICES.filter((s) => s.level === 'pending').length;

  return (
    <div className="space-y-6">

      {/* ── Summary pills ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Active',    value: activeCount,   cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', dot: 'bg-emerald-400' },
          { label: 'Prepared',  value: preparedCount, cls: 'bg-amber-50 border-amber-200 text-amber-700',       dot: 'bg-amber-400' },
          { label: 'Pending',   value: pendingCount,  cls: 'bg-slate-100 border-slate-200 text-slate-600',      dot: 'bg-slate-400' },
        ].map((s) => (
          <div key={s.label} className={`flex items-center gap-3 p-4 rounded-xl border ${s.cls}`}>
            <span className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white/60 shrink-0`}>
              <span className={`w-4 h-4 rounded-full ${s.dot}`}></span>
            </span>
            <div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs font-medium opacity-80">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Legend ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap gap-4 text-xs text-slate-500">
        {[
          { dot: 'bg-emerald-400 animate-pulse', label: 'Active — live and functional' },
          { dot: 'bg-amber-400',                 label: 'Prepared — frontend ready, backend pending' },
          { dot: 'bg-slate-300',                 label: 'Pending — not yet started or blocked' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${l.dot}`}></span>
            {l.label}
          </div>
        ))}
      </div>

      {/* ── Services by category ── */}
      {CATEGORY_ORDER.filter((cat) => byCat[cat]).map((cat) => (
        <div key={cat}>
          <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-4 h-px bg-slate-200 flex-1"></span>
            {cat}
            <span className="w-4 h-px bg-slate-200 flex-1"></span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {byCat[cat].map((svc) => (
              <ServiceCard
                key={svc.key}
                svc={svc}
                tenantCount={svc.tenantKey ? (data.integrationsEnabled[svc.tenantKey] ?? 0) : undefined}
              />
            ))}
          </div>
        </div>
      ))}

      {/* ── Backend roadmap note ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <i className="ri-road-map-line text-blue-500 text-lg mt-0.5 shrink-0"></i>
          <div>
            <p className="text-blue-900 text-sm font-semibold mb-1">Backend Roadmap</p>
            <p className="text-blue-700 text-xs leading-relaxed">
              Services marked <strong>Prepared</strong> have completed frontend implementation and await Edge Function deployment.
              Services marked <strong>Pending</strong> require both backend and frontend Edge Function wiring.
              All API keys and secrets are stored in Supabase Edge Function environment variables — never in frontend code.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
